import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhotoFitBadge from './PhotoFitBadge';
import './PrintQualityRecapModal.css';

// « Avant de commander » (correctif 13, 2026-10-16) — un message calme, jamais dissuasif.
//
// Avant : « 25 points meritent votre attention », deux longs paragraphes, la liste des pages, et un bouton
// « Continuer quand meme » qui sonnait comme une prise de risque. Maintenant :
//   - un titre calme, UNE phrase (« 21 photos risquent d'etre un peu floues a l'impression ») ;
//   - deux boutons : « Commander » (principal) et « Voir les photos concernees » (secondaire) ;
//   - le detail est REPLIE (« Voir le detail »), regroupe par type, avec une action corrective en un clic
//     (« Les afficher plus petites ») ; les explications longues sont dans une bulle d'information ;
//   - jamais ouvert s'il n'y a pas d'alerte serieuse (decide par l'appelant : seuils du correctif 11).
//
// Ne bloque JAMAIS definitivement (§3) : « Commander » reste toujours disponible.
function PrintQualityRecapModal({
  isOpen, warnings = [], onContinueAnyway, onReviewPage, onClose, loading = false,
  // Action corrective en un clic pour les photos floues : async () => { fixed } — l'appelant recontrole ensuite.
  onFitWhole, fixing = false, fixMessage = ''
}) {
  const { t } = useTranslation('preview');
  const [detailOpen, setDetailOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  if (!isOpen) return null;

  const photos = warnings.filter((entry) => entry.kind === 'photo' || (entry.kind == null && entry.kind !== 'texte' && entry.kind !== 'spread'));
  const texts = warnings.filter((entry) => entry.kind === 'texte');
  const firstPage = (list) => (list.length ? list[0].pageIndex : null);

  const groups = [
    { key: 'photos', list: photos, fix: Boolean(onFitWhole) },
    { key: 'texts', list: texts, fix: false }
  ].filter((group) => group.list.length > 0);

  const sentences = [];
  if (photos.length > 0) sentences.push(t('recap.sentencePhotos', { count: photos.length }));
  if (texts.length > 0) sentences.push(t('recap.sentenceTexts', { count: texts.length }));

  const reviewTarget = firstPage(photos) ?? firstPage(texts);

  return (
    <div className="pq-recap-backdrop" onClick={onClose}>
      <div className="pq-recap-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="pq-recap-head">
          <h2 className="pq-recap-title">{t('recap.title')}</h2>
          <button type="button" className="pq-recap-close" onClick={onClose} aria-label={t('recap.close')}>×</button>
        </div>

        <div className="pq-recap-body">
          <p className="pq-recap-intro">
            {sentences.join(' ')}
            <span className="pq-recap-info">
              <button type="button" className="pq-recap-info-btn" onClick={() => setInfoOpen((open) => !open)} aria-expanded={infoOpen} aria-label={t('recap.infoLabel')}>i</button>
            </span>
          </p>
          {infoOpen && <p className="pq-recap-help" role="note">{t('recap.help')}</p>}
          {fixMessage && <p className="pq-recap-fixed" role="status">{fixMessage}</p>}

          <button type="button" className="pq-recap-detail-toggle" onClick={() => setDetailOpen((open) => !open)} aria-expanded={detailOpen}>
            {detailOpen ? t('recap.hideDetail') : t('recap.showDetail')}
          </button>

          {detailOpen && groups.map((group) => (
            <div key={group.key} className="pq-recap-group">
              <div className="pq-recap-group-head">
                <span>{t(`recap.group.${group.key}`, { count: group.list.length })}</span>
                {group.fix && (
                  <button type="button" className="pq-recap-fix" onClick={onFitWhole} disabled={fixing}>
                    {fixing ? t('recap.fixing') : t('recap.fitWhole')}
                  </button>
                )}
              </div>
              <ul className="pq-recap-list">
                {group.list.map((entry) => (
                  <li key={`${entry.pageIndex}-${entry.itemId}`} className="pq-recap-item">
                    {entry.thumbnailUrl ? (
                      <img src={entry.thumbnailUrl} alt="" className="pq-recap-thumb" />
                    ) : (
                      <span className="pq-recap-thumb pq-recap-thumb-empty" aria-hidden="true" />
                    )}
                    <span className="pq-recap-item-text">
                      <span className="pq-recap-item-page">{t('recap.page', { number: entry.pageIndex + 1 })}</span>
                    </span>
                    <PhotoFitBadge fit={entry} size="md" className="pq-recap-item-badge" />
                    {onReviewPage && (
                      <button type="button" className="pq-recap-item-link" onClick={() => onReviewPage(entry.pageIndex)}>
                        {t('recap.review')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pq-recap-actions">
          {onReviewPage && reviewTarget != null && (
            <button type="button" className="btn btn-outline" onClick={() => onReviewPage(reviewTarget)}>
              {photos.length > 0 ? t('recap.seeConcerned') : t('recap.seeConcernedTexts')}
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={onContinueAnyway} disabled={loading}>
            {loading ? t('recap.loading') : t('recap.order')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrintQualityRecapModal;
